import React, { useState, useEffect, useCallback } from 'react';
import { leadsAPI } from '../lib/api';
import { formatDate } from '../lib/utils';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Loader2, 
  ChevronLeft, 
  ChevronRight,
  Users,
  Mail,
  Phone,
  Building2,
  Eye,
  EyeOff
} from 'lucide-react';

const Leads = () => {
  const [leads, setLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [activeTab, setActiveTab] = useState('unread');
  
  // Detail modal
  const [selectedLead, setSelectedLead] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  const pageSize = 10;

  useEffect(() => {
    fetchLeads();
  }, [page, activeTab]);

  const fetchLeads = useCallback(async () => {
    setIsLoading(true);
    try {
      const isRead = activeTab === 'read' ? true : activeTab === 'unread' ? false : null;
      const response = await leadsAPI.getLeads(page, pageSize, isRead);
      setLeads(response.data.items);
      setTotalPages(response.data.total_pages);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to fetch leads');
    } finally {
      setIsLoading(false);
    }
  }, [page, activeTab]);

  const handleMarkRead = async (lead) => {
    try {
      await leadsAPI.markLeadRead(lead.id);
      toast.success('Marked as read');
      fetchLeads();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleMarkUnread = async (lead) => {
    try {
      await leadsAPI.markLeadUnread(lead.id);
      toast.success('Marked as unread');
      fetchLeads();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const openDetail = (lead) => {
    setSelectedLead(lead);
    setIsDetailOpen(true);
    
    // Auto mark as read when viewing
    if (!lead.is_read) {
      handleMarkRead(lead);
    }
  };

  return (
    <div className="animate-fade-in" data-testid="leads-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-red-50 rounded-lg">
            <Users className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h1 
              className="text-2xl font-bold text-slate-900"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Leads
            </h1>
            <p className="text-sm text-slate-500">{total} total leads</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(value) => { setActiveTab(value); setPage(1); }}>
        <TabsList className="mb-4">
          <TabsTrigger value="unread" data-testid="tab-unread">
            Unread
          </TabsTrigger>
          <TabsTrigger value="read" data-testid="tab-read">
            Read
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab}>
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : leads.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                <Users className="w-12 h-12 mb-4 opacity-20" />
                <p>No {activeTab} leads found</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Status</th>
                        <th>Name</th>
                        <th>Company</th>
                        <th>Email</th>
                        <th>Mobile</th>
                        <th>Message</th>
                        <th>Date</th>
                        <th className="text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leads.map((lead) => (
                        <tr 
                          key={lead.id} 
                          className={!lead.is_read ? 'bg-amber-50/50' : ''}
                          data-testid={`lead-row-${lead.id}`}
                        >
                          <td>
                            <span className={`badge ${lead.is_read ? 'badge-read' : 'badge-unread'}`}>
                              {lead.is_read ? 'Read' : 'Unread'}
                            </span>
                          </td>
                          <td className="font-medium text-slate-900">{lead.name}</td>
                          <td>{lead.company || '-'}</td>
                          <td>
                            <a href={`mailto:${lead.email}`} className="text-sky-600 hover:underline text-sm">
                              {lead.email}
                            </a>
                          </td>
                          <td>
                            <a href={`tel:${lead.mobile_number}`} className="text-sky-600 hover:underline text-sm">
                              {lead.mobile_number}
                            </a>
                          </td>
                          <td className="max-w-xs truncate text-sm text-slate-500">
                            {lead.message || '-'}
                          </td>
                          <td className="text-xs">{formatDate(lead.created_at)}</td>
                          <td className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openDetail(lead)}
                                data-testid={`view-btn-${lead.id}`}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              {lead.is_read ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleMarkUnread(lead)}
                                  data-testid={`mark-unread-btn-${lead.id}`}
                                >
                                  <EyeOff className="w-4 h-4" />
                                </Button>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleMarkRead(lead)}
                                  data-testid={`mark-read-btn-${lead.id}`}
                                >
                                  <Eye className="w-4 h-4 text-green-600" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
                    <p className="text-sm text-slate-500">
                      Page {page} of {totalPages}
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                        data-testid="prev-page-btn"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                        data-testid="next-page-btn"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-lg" data-testid="lead-detail-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Lead Details
            </DialogTitle>
          </DialogHeader>
          {selectedLead && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-2 pb-4 border-b border-slate-200">
                <span className={`badge ${selectedLead.is_read ? 'badge-read' : 'badge-unread'}`}>
                  {selectedLead.is_read ? 'Read' : 'Unread'}
                </span>
                <span className="text-sm text-slate-500">
                  {formatDate(selectedLead.created_at)}
                </span>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Name</p>
                  <p className="font-medium">{selectedLead.name}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Company</p>
                  <p className="font-medium flex items-center gap-1">
                    <Building2 className="w-4 h-4 text-slate-400" />
                    {selectedLead.company || '-'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Email</p>
                  <p className="font-medium flex items-center gap-1">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <a href={`mailto:${selectedLead.email}`} className="text-sky-600 hover:underline">
                      {selectedLead.email}
                    </a>
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Mobile</p>
                  <p className="font-medium flex items-center gap-1">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <a href={`tel:${selectedLead.mobile_number}`} className="text-sky-600 hover:underline">
                      {selectedLead.mobile_number}
                    </a>
                  </p>
                </div>
              </div>

              {selectedLead.message && (
                <div className="pt-4 border-t border-slate-200">
                  <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Message</p>
                  <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg whitespace-pre-wrap">
                    {selectedLead.message}
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4">
                {selectedLead.is_read ? (
                  <Button
                    variant="outline"
                    onClick={() => {
                      handleMarkUnread(selectedLead);
                      setIsDetailOpen(false);
                    }}
                  >
                    <EyeOff className="w-4 h-4 mr-2" />
                    Mark as Unread
                  </Button>
                ) : (
                  <Button
                    onClick={() => {
                      handleMarkRead(selectedLead);
                      setIsDetailOpen(false);
                    }}
                    className="bg-slate-900 hover:bg-slate-800"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Mark as Read
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Leads;
